import { supabase } from "@/lib/supabase";

/**
 * News Service for fetching articles from Supabase
 */

// Transform Supabase article data to match our component expectations
const transformArticle = (article) => {
  if (!article) return null;

  return {
    id: article.id,
    title: article.title,
    excerpt: article.excerpt,
    slug: article.slug,
    category: article.category,
    tags: article.tags || [],
    date: article.date,
    publishedAt: article.published_at,
    updatedAt: article.updated_at,
    author: article.author,
    image: article.image,
    content: article.content,
    readTime: article.read_time,
    wordCount: article.word_count,
    seo: article.seo,
    status: article.status,
    featured: article.featured,
    relatedArticles: article.related_articles || [],
    createdAt: article.created_at,

    // Legacy compatibility fields
    imageUrl: article.image?.url,
  };
};

/**
 * Fetch all published articles with optional filtering
 * @param {Object} options - Query options
 * @param {string} options.category - Filter by category
 * @param {boolean} options.featured - Filter by featured status
 * @param {Array} options.tags - Filter by tags (topics)
 * @param {Array} options.years - Filter by years
 * @param {string} options.sortBy - Sort order: 'newest', 'oldest', 'alphabetical-az', 'alphabetical-za'
 * @param {number} options.limit - Limit number of results
 * @param {number} options.offset - Offset for pagination
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const fetchArticles = async (options = {}) => {
  try {
    let query = supabase
      .from("news_articles")
      .select("*")
      .eq("status", "published");

    // Apply filters
    if (options.category && options.category !== "all") {
      // Convert category ID back to proper case format
      // "research" -> "Research", "global-affairs" -> "Global Affairs"
      const categoryName = options.category
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      query = query.eq("category", categoryName);
    }

    if (options.featured !== undefined) {
      query = query.eq("featured", options.featured);
    }

    // Apply sorting
    switch (options.sortBy) {
      case "oldest":
        query = query.order("date", { ascending: true });
        break;
      case "alphabetical-az":
        query = query.order("title", { ascending: true });
        break;
      case "alphabetical-za":
        query = query.order("title", { ascending: false });
        break;
      case "newest":
      default:
        query = query.order("date", { ascending: false });
        break;
    }

    // Apply pagination
    if (options.limit) {
      query = query.limit(options.limit);
    }

    if (options.offset) {
      query = query.range(
        options.offset,
        options.offset + (options.limit || 10) - 1
      );
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching articles:", error);
      return { data: [], error };
    }

    // Transform the data
    let transformedData = data.map(transformArticle);

    // Apply client-side filtering for tags and years (since Supabase doesn't support array filtering easily)
    if (options.tags && options.tags.length > 0) {
      transformedData = transformedData.filter((article) => {
        if (!article.tags || !Array.isArray(article.tags)) return false;
        return options.tags.some((tag) =>
          article.tags.some((articleTag) =>
            articleTag.toLowerCase().includes(tag.toLowerCase())
          )
        );
      });
    }

    if (options.years && options.years.length > 0) {
      transformedData = transformedData.filter((article) => {
        if (!article.date) return false;
        const articleYear = new Date(article.date).getFullYear().toString();
        return options.years.includes(articleYear);
      });
    }

    return { data: transformedData, error: null };
  } catch (error) {
    console.error("Exception in fetchArticles:", error);
    return { data: [], error };
  }
};

/**
 * Fetch articles and category counts in a single operation
 * This combines fetchArticles and fetchArticleCategories to avoid double API calls
 * @param {Object} options - Query options (same as fetchArticles)
 * @returns {Promise<{articles: Array, categories: Array, error: Error|null}>}
 */
export const fetchArticlesWithCategoryCounts = async (options = {}) => {
  try {
    // Single query to get all published articles
    let query = supabase
      .from("news_articles")
      .select("*")
      .eq("status", "published");

    // Apply sorting
    switch (options.sortBy) {
      case "oldest":
        query = query.order("date", { ascending: true });
        break;
      case "alphabetical-az":
        query = query.order("title", { ascending: true });
        break;
      case "alphabetical-za":
        query = query.order("title", { ascending: false });
        break;
      case "newest":
      default:
        query = query.order("date", { ascending: false });
        break;
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching articles:", error);
      return { articles: [], categories: [], error };
    }

    // Transform all articles
    const allArticles = data.map(transformArticle);

    // Calculate category counts from the fetched data
    const categoryCounts = allArticles.reduce((acc, article) => {
      acc[article.category] = (acc[article.category] || 0) + 1;
      return acc;
    }, {});

    const categories = [
      { id: "all", label: "All", count: allArticles.length },
      ...Object.entries(categoryCounts).map(([category, count]) => ({
        id: category.toLowerCase().replace(/\s+/g, "-"),
        label: category,
        count,
      })),
    ];

    // Apply client-side filtering for category, tags, and years
    let filteredArticles = allArticles;

    if (options.category && options.category !== "all") {
      const categoryName = options.category
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      filteredArticles = filteredArticles.filter(
        (article) => article.category === categoryName
      );
    }

    if (options.tags && options.tags.length > 0) {
      filteredArticles = filteredArticles.filter((article) => {
        if (!article.tags || !Array.isArray(article.tags)) return false;
        return options.tags.some((tag) =>
          article.tags.some((articleTag) =>
            articleTag.toLowerCase().includes(tag.toLowerCase())
          )
        );
      });
    }

    if (options.years && options.years.length > 0) {
      filteredArticles = filteredArticles.filter((article) => {
        if (!article.date) return false;
        const articleYear = new Date(article.date).getFullYear().toString();
        return options.years.includes(articleYear);
      });
    }

    return { articles: filteredArticles, categories, error: null };
  } catch (error) {
    console.error("Exception in fetchArticlesWithCategoryCounts:", error);
    return { articles: [], categories: [], error };
  }
};

/**
 * Fetch a single article by slug or ID
 * @param {string} identifier - Article slug or ID
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const fetchArticleBySlug = async (identifier) => {
  try {
    // Try to fetch by slug first, then by ID
    let { data, error } = await supabase
      .from("news_articles")
      .select("*")
      .eq("slug", identifier)
      .eq("status", "published")
      .single();

    // If not found by slug, try by ID
    if (!data && !error) {
      const result = await supabase
        .from("news_articles")
        .select("*")
        .eq("id", identifier)
        .eq("status", "published")
        .single();

      data = result.data;
      error = result.error;
    }

    if (error) {
      console.error("Error fetching article:", error);
      return { data: null, error };
    }

    if (!data) {
      return { data: null, error: new Error("Article not found") };
    }

    // Transform the data
    const transformedData = transformArticle(data);

    return { data: transformedData, error: null };
  } catch (error) {
    console.error("Exception in fetchArticleBySlug:", error);
    return { data: null, error };
  }
};

/**
 * Fetch featured articles
 * @param {number} limit - Number of featured articles to fetch
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const fetchFeaturedArticles = async (limit = 3) => {
  return fetchArticles({ featured: true, limit });
};

/**
 * Fetch articles by category
 * @param {string} category - Category to filter by
 * @param {number} limit - Number of articles to fetch
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const fetchArticlesByCategory = async (category, limit = 10) => {
  return fetchArticles({ category, limit });
};

/**
 * Fetch related articles for a given article
 * @param {string} articleId - Current article ID
 * @param {Array} relatedIds - Array of related article IDs
 * @param {number} limit - Maximum number of related articles to fetch
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const fetchRelatedArticles = async (
  articleId,
  relatedIds = [],
  limit = 3
) => {
  try {
    if (!relatedIds || relatedIds.length === 0) {
      // If no specific related articles, fetch articles from the same category
      const { data: currentArticle } = await fetchArticleBySlug(articleId);
      if (currentArticle) {
        return fetchArticlesByCategory(currentArticle.category, limit);
      }
      return { data: [], error: null };
    }

    const { data, error } = await supabase
      .from("news_articles")
      .select("*")
      .in("id", relatedIds)
      .eq("status", "published")
      .limit(limit);

    if (error) {
      console.error("Error fetching related articles:", error);
      return { data: [], error };
    }

    // Transform the data
    const transformedData = data.map(transformArticle);

    return { data: transformedData, error: null };
  } catch (error) {
    console.error("Exception in fetchRelatedArticles:", error);
    return { data: [], error };
  }
};

/**
 * Search articles by title or content
 * @param {string} searchTerm - Search term
 * @param {number} limit - Number of results to return
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const searchArticles = async (searchTerm, limit = 10) => {
  try {
    const { data, error } = await supabase
      .from("news_articles")
      .select("*")
      .eq("status", "published")
      .or(`title.ilike.%${searchTerm}%,excerpt.ilike.%${searchTerm}%`)
      .order("date", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("Error searching articles:", error);
      return { data: [], error };
    }

    // Transform the data
    const transformedData = data.map(transformArticle);

    return { data: transformedData, error: null };
  } catch (error) {
    console.error("Exception in searchArticles:", error);
    return { data: [], error };
  }
};

/**
 * Get article categories with counts
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const fetchArticleCategories = async () => {
  try {
    const { data, error } = await supabase
      .from("news_articles")
      .select("category")
      .eq("status", "published");

    if (error) {
      console.error("Error fetching categories:", error);
      return { data: [], error };
    }

    // Count articles by category
    const categoryCounts = data.reduce((acc, article) => {
      acc[article.category] = (acc[article.category] || 0) + 1;
      return acc;
    }, {});

    // Transform to array format
    const categories = Object.entries(categoryCounts).map(
      ([category, count]) => ({
        id: category.toLowerCase().replace(/\s+/g, "-"),
        label: category,
        count,
      })
    );

    // Add "All" category
    const totalCount = data.length;
    const allCategories = [
      { id: "all", label: "All", count: totalCount },
      ...categories,
    ];

    return { data: allCategories, error: null };
  } catch (error) {
    console.error("Exception in fetchArticleCategories:", error);
    return { data: [], error };
  }
};

export default {
  fetchArticles,
  fetchArticlesWithCategoryCounts,
  fetchArticleBySlug,
  fetchFeaturedArticles,
  fetchArticlesByCategory,
  fetchRelatedArticles,
  searchArticles,
  fetchArticleCategories,
};
